import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Trash2, X, type LucideIcon } from "lucide-react";
import "./ConfirmationModal.css";

export interface ConfirmationModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info";
  isLoading?: boolean;
  icon?: LucideIcon;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText,
  cancelText,
  variant = "danger",
  isLoading = false,
  icon: CustomIcon,
}) => {
  const { t } = useTranslation("common");
  const modalRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // Focus trap and escape key handling
  useEffect(() => {
    if (!isOpen) return;

    // Focus the cancel button when modal opens
    cancelButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isLoading) {
        onCancel();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, isLoading, onCancel]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onCancel();
    }
  };

  if (!isOpen) return null;

  const IconComponent =
    CustomIcon || (variant === "danger" ? Trash2 : AlertTriangle);

  return (
    <div
      className="confirmation-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmation-title"
      aria-describedby="confirmation-message"
    >
      <div
        className={`confirmation-modal confirmation-modal--${variant}`}
        ref={modalRef}
      >
        {/* Close button */}
        <button
          className="confirmation-modal-close"
          onClick={onCancel}
          disabled={isLoading}
          aria-label={t("actions.close")}
        >
          <X size={20} />
        </button>

        {/* Icon */}
        <div
          className={`confirmation-modal-icon confirmation-modal-icon--${variant}`}
        >
          <IconComponent size={28} strokeWidth={2} />
        </div>

        {/* Content */}
        <div className="confirmation-modal-content">
          <h2 id="confirmation-title" className="confirmation-modal-title">
            {title}
          </h2>
          <div id="confirmation-message" className="confirmation-modal-message">
            {message}
          </div>
        </div>

        {/* Actions */}
        <div className="confirmation-modal-actions">
          <button
            ref={cancelButtonRef}
            className="confirmation-btn confirmation-btn--secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelText ?? t("actions.cancel")}
          </button>
          <button
            className={`confirmation-btn confirmation-btn--${variant}`}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="confirmation-spinner" />
                {t("loading.processing")}
              </>
            ) : (
              <>
                <IconComponent size={16} />
                {confirmText ?? t("actions.confirm")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
